import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { getRequestGlobal } from "../../API/global";

const FormView = ({ formData, showDeclaration = 0 }) => {
    const [locationData, setLocationData] = useState({
        countryName: '',
        stateName: '',
        cityName: ''
    });
    const [loading, setLoading] = useState(true);

    // Helper function to parse JSON strings safely
    const parseJSON = (str, defaultValue = []) => {
        if (!str) return defaultValue;
        try {
            // Handle the malformed ai_stages object
            if (str === '[object Object]') return defaultValue;
            return JSON.parse(str);
        } catch (e) {
            return defaultValue;
        }
    };

    // Helper to convert string boolean to actual boolean
    const getBooleanValue = (value) => {
        if (typeof value === 'boolean') return value;
        return value === 'true';
    };

    // Parse the data
    const aiTools = (formData.ai_tools);
    const teamMembers = (formData.team_members);

    // AI Stages data
    const aiStages = {
        concept_script_development: getBooleanValue(formData.concept_script_development),
        storyboarding_pre_visualization: getBooleanValue(formData.storyboarding_pre_visualization),
        video_generation_animation: getBooleanValue(formData.video_generation_animation),
        editing_post_production: getBooleanValue(formData.editing_post_production),
        sound_voice_music: getBooleanValue(formData.sound_voice_music),
        other: getBooleanValue(formData.other)
    };

    const selectedStages = Object.entries(aiStages)
        .filter(([_, value]) => value)
        .map(([key]) => key);

    // Fetch location names from IDs
    useEffect(() => {
        const fetchLocationNames = async () => {
            try {
                setLoading(true);
                const promises = [];

                // Fetch country name
                if (formData.country) {
                    promises.push(
                        getRequestGlobal("country", {})
                            .then(response => {
                                const countries = response.data || {};
                                return countries[formData.country] || formData.country;
                            })
                            .catch(() => formData.country)
                    );
                } else {
                    promises.push(Promise.resolve(''));
                }

                // Fetch state name (only for India)
                if (formData.country === "101" && formData.permanent_state) {
                    promises.push(
                        getRequestGlobal("state", {})
                            .then(response => {
                                const states = response.data || {};
                                return states[formData.permanent_state] || formData.permanent_state;
                            })
                            .catch(() => formData.permanent_state)
                    );
                } else {
                    promises.push(Promise.resolve(''));
                }

                // Fetch city name (only for India and if state is selected)
                if (formData.country === "101" && formData.permanent_state && formData.permanent_city) {
                    promises.push(
                        getRequestGlobal("city", { state_id: formData.permanent_state })
                            .then(response => {
                                const cities = response.data || {};
                                return cities[formData.permanent_city] || formData.permanent_city;
                            })
                            .catch(() => formData.permanent_city)
                    );
                } else {
                    promises.push(Promise.resolve(''));
                }

                const [countryName, stateName, cityName] = await Promise.all(promises);

                setLocationData({
                    countryName,
                    stateName,
                    cityName
                });
            } catch (error) {
                console.error('Error fetching location data:', error);
                // Fallback to IDs if API fails
                setLocationData({
                    countryName: formData.country || '',
                    stateName: formData.permanent_state || '',
                    cityName: formData.permanent_city || ''
                });
            } finally {
                setLoading(false);
            }
        };

        if (formData.film_title) {
            fetchLocationNames();
        }
    }, [formData.country, formData.permanent_state, formData.permanent_city, formData.film_title]);

    if (!formData.film_title) {
        return null;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#1c5768', mb: 4 }}>
                Application Preview
            </Typography>

            {/* Entry Details */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1c5768' }}>
                        STEP 1: Entry Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Film Title</Typography>
                            <Typography variant="body1" gutterBottom>{formData.film_title}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Duration</Typography>
                            <Typography variant="body1" gutterBottom>{formData.duration} minutes</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Genre/Theme</Typography>
                            <Typography variant="body1" gutterBottom>{formData.genre_theme}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">Synopsis</Typography>
                            <Typography variant="body1" gutterBottom>{formData.synopsis}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">Creative Note</Typography>
                            <Typography variant="body1" gutterBottom>{formData.creative_note}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">Human-AI Collaboration</Typography>
                            <Typography variant="body1" gutterBottom>{formData.ai_collaboration}</Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Team Information */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1c5768' }}>
                        STEP 2: Team Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Team Name</Typography>
                            <Typography variant="body1" gutterBottom>{formData.team_name}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Team Leader</Typography>
                            <Typography variant="body1" gutterBottom>{formData.team_leader}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                            <Typography variant="body1" gutterBottom>{formData.email}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                            <Typography variant="body1" gutterBottom>{formData.phone}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Organization</Typography>
                            <Typography variant="body1" gutterBottom>{formData.organization || 'Not specified'}</Typography>
                        </Grid>

                        {/* Location Information */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Country</Typography>
                            <Typography variant="body1" gutterBottom>
                                {loading ? 'Loading...' : locationData.countryName || 'Not specified'}
                            </Typography>
                        </Grid>

                        {formData.country === "101" && formData.permanent_state && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">State</Typography>
                                <Typography variant="body1" gutterBottom>
                                    {loading ? 'Loading...' : locationData.stateName || formData.permanent_state}
                                </Typography>
                            </Grid>
                        )}

                        {formData.country === "101" && formData.permanent_city && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">City</Typography>
                                <Typography variant="body1" gutterBottom>
                                    {loading ? 'Loading...' : locationData.cityName || formData.permanent_city}
                                </Typography>
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                            <Typography variant="body1" gutterBottom>{formData.address || 'Not specified'}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Zipcode</Typography>
                            <Typography variant="body1" gutterBottom>{formData.zipcode || 'Not specified'}</Typography>
                        </Grid>
                    </Grid>

                    <Typography variant="h6" gutterBottom>Team Members</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Mobile</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {teamMembers.map((member, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{member.team_members_name}</TableCell>
                                        <TableCell>{member.team_members_email}</TableCell>
                                        <TableCell>{member.team_members_role}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* AI Tools */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1c5768' }}>
                        STEP 3: AI Tools & Platforms
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Tool Name</TableCell>
                                    <TableCell>Purpose/Function</TableCell>
                                    <TableCell>Version</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {aiTools.map((tool, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{tool.tool_name}</TableCell>
                                        <TableCell>{tool.purpose_function}</TableCell>
                                        <TableCell>{tool.version || 'Not specified'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* AI Stages */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1c5768' }}>
                        STEP 4: AI Stages Used
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Selected Production Stages:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedStages.map((stage) => (
                                <Chip
                                    key={stage}
                                    label={stage.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Display descriptions for selected stages */}
                    {aiStages.concept_script_development && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Concept / Script Development
                            </Typography>
                            <Typography variant="body2">
                                {formData.concept_script_development_description}
                            </Typography>
                        </Box>
                    )}

                    {aiStages.storyboarding_pre_visualization && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Storyboarding / Pre-visualization
                            </Typography>
                            <Typography variant="body2">
                                {formData.storyboarding_pre_visualization_description}
                            </Typography>
                        </Box>
                    )}

                    {aiStages.video_generation_animation && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Video Generation / Animation
                            </Typography>
                            <Typography variant="body2">
                                {formData.video_generation_animation_description}
                            </Typography>
                        </Box>
                    )}

                    {aiStages.editing_post_production && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Editing / Post-production
                            </Typography>
                            <Typography variant="body2">
                                {formData.editing_post_production_description}
                            </Typography>
                        </Box>
                    )}

                    {aiStages.sound_voice_music && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Sound / Voice / Music
                            </Typography>
                            <Typography variant="body2">
                                {formData.sound_voice_music_description}
                            </Typography>
                        </Box>
                    )}

                    {aiStages.other && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Other Applications
                            </Typography>
                            <Typography variant="body2">
                                {formData.other_description}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Submission Links */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1c5768' }}>
                        Submission Links
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Film Link</Typography>
                            <Typography variant="body1" gutterBottom>{formData.film_link}</Typography>
                            {formData.film_link_password && (
                                <Typography variant="body2" color="textSecondary">
                                    Password: {formData.film_link_password}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Creative Note Link</Typography>
                            <Typography variant="body1" gutterBottom>{formData.creative_note_link}</Typography>
                            {formData.creative_note_password && (
                                <Typography variant="body2" color="textSecondary">
                                    Password: {formData.creative_note_password}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Declaration */}
            {showDeclaration ?
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h5" gutterBottom sx={{ color: '#1c5768' }}>
                            STEP 5: Compliance & Declaration
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Ethical Compliance</Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ All AI-generated content is original or properly licensed
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ No copyrighted, harmful, or misleading material has been used
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ Consent obtained for any real likeness, voices, or data used
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ The work upholds Responsible and Ethical AI Storytelling Practices
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Declaration</Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ The submitted work is original and created by our team
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ All AI tools and creative processes have been accurately disclosed
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ The film aligns with Responsible AI Storytelling Practices
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                ✓ LTIM, NFDC, and IFFI may screen and promote our work
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Digital Signature</Typography>
                                <Typography variant="body1" gutterBottom>{formData.digital_signature}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                                <Typography variant="body1" gutterBottom>{formData.declaration_date}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">Place</Typography>
                                <Typography variant="body1" gutterBottom>{formData.declaration_place}</Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                : <></>
            }
        </Box>
    )
}

export default FormView;